import React, { useEffect, useState } from "react";
import { MessageSquare, Send, EyeOff, Eye, Trash2, CheckCheck, MessageSquareOff, Filter, Save, Loader2 } from "lucide-react";
import { api } from "../../lib/api";
import { D, JAKARTA, inputStyle, Card, SectionTitle, Btn, Chip, Msg, fmtTime, namaKota } from "./ui";
import { IconBtn } from "./RadioPanel";
import { InstagramPanel } from "./InstagramPanel";

const ROLE = {
  warga: { label: "Warga", tone: "green" }, mayor: { label: "Walikota", tone: "blue" }, admin: { label: "Admin", tone: "gray" },
};

// Panel Pesan Warga: pantau feed global (sembunyikan / hapus / tandai dibaca / blokir pengirim dari chat) + filter kata + Instagram DM.
// Data akun warga lengkap ada di tab "Warga" (Database Warga).
export function PesanPanel({ token }) {
  const [feed, setFeed] = useState({ pesan: [], unread: 0, filter: null });
  const [msg, setMsg] = useState("");
  const load = () => api.adminPesan(token).then(setFeed).catch(e => setMsg(e.message));
  useEffect(() => { load(); }, []); // eslint-disable-line
  const cityName = id => (id ? namaKota(id) : "Jawsel");

  return (
    <div style={{ padding: "22px 20px 30px", fontFamily: JAKARTA }}>
      <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 18, flexWrap: "wrap" }}>
        <h2 style={{ margin: 0, fontSize: 27, fontWeight: 800, letterSpacing: -0.6, color: D.ink }}>Pesan Warga</h2>
        <Chip tone={feed.unread ? "red" : "gray"} testid="pesan-unread">{feed.unread} belum dibaca</Chip>
        {feed.filter && <Chip tone={feed.filter.kata.length ? "amber" : "gray"} testid="pesan-filter-chip">{feed.filter.kata.length} kata disaring · {feed.filter.mode === "tolak" ? "ditolak" : "disensor"}</Chip>}
        <Msg text={msg} testid="pesan-msg" />
      </div>
      <div style={{ display: "grid", gridTemplateColumns: "minmax(0, 1.25fr) minmax(0, 1fr)", gap: 18, alignItems: "start" }}>
        <Feed token={token} feed={feed} reload={load} setMsg={setMsg} cityName={cityName} />
        <div style={{ display: "flex", flexDirection: "column", gap: 18 }}>
          <FilterKata token={token} awal={feed.filter} onSaved={load} />
          <InstagramPanel token={token} setMsg={setMsg} onWargaChanged={load} />
        </div>
      </div>
    </div>
  );
}

function Feed({ token, feed, reload, setMsg, cityName }) {
  const [text, setText] = useState("");
  async function send(e) {
    e.preventDefault();
    if (!text.trim()) return;
    try { await api.adminPostPesan(token, text.trim()); setText(""); reload(); } catch (err) { setMsg(err.message); }
  }
  return (
    <Card testid="pesan-feed-card">
      <SectionTitle title="Feed seluruh Jawsel" sub="Pesan warga asli, walikota, dan admin. Sembunyikan / hapus pesan yang tidak pantas, atau blokir pengirimnya dari Pesan Warga."
        right={<Btn variant="ghost" small testid="pesan-read-all" onClick={() => api.readAllPesan(token).then(reload).catch(e => setMsg(e.message))}><CheckCheck size={14} /> Tandai semua dibaca</Btn>} />
      <form onSubmit={send} data-testid="pesan-form" style={{ display: "flex", gap: 8, marginBottom: 14 }}>
        <input data-testid="pesan-input" value={text} onChange={e => setText(e.target.value)} maxLength={500} placeholder="Tulis pesan sebagai Admin Jawsel..." style={inputStyle} />
        <Btn type="submit" testid="pesan-send" disabled={!text.trim()}><Send size={14} /> Kirim</Btn>
      </form>
      <div data-testid="pesan-list" style={{ display: "flex", flexDirection: "column", gap: 8, maxHeight: "62vh", overflowY: "auto", paddingRight: 4 }}>
        {feed.pesan.map(p => {
          const r = ROLE[p.author?.role] || ROLE.admin;
          return (
            <div key={p.id} data-testid={`pesan-${p.id}`} style={{ padding: "12px 14px", borderRadius: 16, border: `1.5px solid ${p.read ? D.line : D.blue}`, background: p.hidden ? "#faf7ee" : "#fff", opacity: p.hidden ? .7 : 1 }}>
              <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
                <Chip tone={r.tone}>{r.label}</Chip>
                <b style={{ fontSize: 13.5, color: D.ink }}>{p.author?.nama}</b>
                <span style={{ fontSize: 12, color: D.sub }}>{cityName(p.author?.city_id)} · {fmtTime(p.created_at)}</span>
                {p.hidden && <Chip tone="red" testid={`pesan-hidden-${p.id}`}>Disembunyikan</Chip>}
                {p.disensor && <Chip tone="amber" testid={`pesan-disensor-${p.id}`}>Disensor filter</Chip>}
                {p.author_chat_diblokir && <Chip tone="red" testid={`pesan-pengirim-diblokir-${p.id}`}>Pengirim diblokir</Chip>}
                {!p.read && <Chip tone="blue">Baru</Chip>}
                <div style={{ flex: 1 }} />
                {p.author?.role === "warga" && (p.author_chat_diblokir
                  ? <IconBtn testid={`pesan-buka-blokir-${p.id}`} title="Izinkan pengirim mengirim pesan lagi" onClick={() => api.adminWargaChat(token, p.author.id, false).then(reload).catch(e => setMsg(e.message))}><MessageSquare size={14} /></IconBtn>
                  : <IconBtn testid={`pesan-blokir-${p.id}`} title="Blokir pengirim dari Pesan Warga" danger onClick={() => { if (window.confirm(`Blokir ${p.author.nama} dari Pesan Warga? Akun & KTW-nya tetap aktif.`)) api.adminWargaChat(token, p.author.id, true).then(reload).catch(e => setMsg(e.message)); }}><MessageSquareOff size={14} /></IconBtn>)}
                <IconBtn testid={`pesan-toggle-${p.id}`} title={p.hidden ? "Tampilkan" : "Sembunyikan"} onClick={() => api.patchPesan(token, p.id, { hidden: !p.hidden, read: true }).then(reload).catch(e => setMsg(e.message))}>{p.hidden ? <Eye size={14} /> : <EyeOff size={14} />}</IconBtn>
                <IconBtn testid={`pesan-delete-${p.id}`} danger title="Hapus pesan" onClick={() => { if (window.confirm("Hapus pesan ini?")) api.deletePesan(token, p.id).then(reload).catch(e => setMsg(e.message)); }}><Trash2 size={14} /></IconBtn>
              </div>
              <p style={{ margin: "8px 0 0", fontSize: 13.5, color: D.ink, lineHeight: 1.5, whiteSpace: "pre-wrap" }}>{p.text}</p>
            </div>
          );
        })}
        {feed.pesan.length === 0 && <p data-testid="pesan-empty" style={{ color: D.sub, fontSize: 13, margin: 0 }}><MessageSquare size={13} style={{ verticalAlign: -2 }} /> Belum ada pesan.</p>}
      </div>
    </Card>
  );
}

// Filter kata: daftar kata terlarang (satu per baris / dipisah koma) + mode sensor (b****) atau tolak (pesan tidak terkirim).
function FilterKata({ token, awal, onSaved }) {
  const [teks, setTeks] = useState(""), [mode, setMode] = useState("sensor"), [busy, setBusy] = useState(false), [info, setInfo] = useState("");
  useEffect(() => { if (awal) { setTeks(awal.kata.join("\n")); setMode(awal.mode); } }, [awal]);
  const kata = teks.split(/[\n,]+/).map(k => k.trim().toLowerCase()).filter(Boolean);
  const simpan = async e => {
    e.preventDefault(); setBusy(true); setInfo("");
    try { const r = await api.adminSimpanPesanFilter(token, { kata, mode }); setTeks(r.kata.join("\n")); setInfo(`Tersimpan — ${r.kata.length} kata, mode ${r.mode}.`); onSaved(); }
    catch (x) { setInfo(x.message); }
    setBusy(false);
  };
  const radio = (v, label, ket) => (
    <label data-testid={`filter-mode-${v}`} style={{ flex: 1, display: "flex", gap: 8, alignItems: "flex-start", padding: "10px 12px", borderRadius: 14, border: `1.5px solid ${mode === v ? D.blue : D.line}`, background: mode === v ? D.blueSoft : "#fff", cursor: "pointer" }}>
      <input type="radio" name="filter-mode" checked={mode === v} onChange={() => setMode(v)} style={{ marginTop: 2, accentColor: D.blue }} />
      <span style={{ lineHeight: 1.3 }}><b style={{ fontSize: 12.5, color: D.ink }}>{label}</b><br /><span style={{ fontSize: 11.5, color: D.sub }}>{ket}</span></span>
    </label>
  );
  return (
    <Card testid="filter-kata-card">
      <SectionTitle title="Filter kata" sub="Kata di daftar ini tidak akan tampil apa adanya di Pesan Warga (berlaku untuk warga & walikota). Pencocokan tidak peka huruf besar dan tahan trik b.o.d.o.h." />
      <form onSubmit={simpan} style={{ display: "flex", flexDirection: "column", gap: 10 }}>
        <textarea data-testid="filter-kata-teks" value={teks} onChange={e => setTeks(e.target.value)} rows={5} placeholder={"satu kata per baris, mis.\nkasar\njudi\npinjol"} style={{ ...inputStyle, width: "100%", resize: "vertical", lineHeight: 1.5, fontFamily: JAKARTA }} />
        <div style={{ display: "flex", gap: 8 }}>
          {radio("sensor", "Sensor otomatis", "kata diganti bintang: b****, pesan tetap terkirim")}
          {radio("tolak", "Tolak pesan", "pengirim diberi tahu pesannya mengandung kata terlarang")}
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <Btn type="submit" testid="filter-kata-simpan" disabled={busy}>{busy ? <Loader2 size={14} className="spin" /> : <Save size={14} />} Simpan filter</Btn>
          <span style={{ fontSize: 12, color: D.sub, fontWeight: 700 }}><Filter size={12} style={{ verticalAlign: -2 }} /> {kata.length} kata</span>
          {info && <span data-testid="filter-kata-info" style={{ fontSize: 12, color: D.chipInk, fontWeight: 700 }}>{info}</span>}
        </div>
      </form>
    </Card>
  );
}
